import firebase from '@/firebase/config'
import 'firebase/firestore'

const db = firebase.firestore()

/**
 * Prefer one explanation doc when duplicates share the same questionId.
 * Prefer textHtml, then net votes, then stable doc id.
 */
const pickPreferredExplanationDoc = (docs) => {
  if (!docs || docs.length === 0) return null
  return [...docs].sort((a, b) => {
    const aHtml = (a.data().textHtml || '').trim() ? 1 : 0
    const bHtml = (b.data().textHtml || '').trim() ? 1 : 0
    if (aHtml !== bHtml) return bHtml - aHtml

    const aScore = (Number(a.data().upvotes) || 0) - (Number(a.data().downvotes) || 0)
    const bScore = (Number(b.data().upvotes) || 0) - (Number(b.data().downvotes) || 0)
    if (aScore !== bScore) return bScore - aScore

    const aUp = Number(a.data().upvotes) || 0
    const bUp = Number(b.data().upvotes) || 0
    if (aUp !== bUp) return bUp - aUp

    return a.id < b.id ? -1 : 1
  })[0]
}

/**
 * Delete sibling explanation docs with the same questionId, keeping `keepId`.
 */
export const deleteSiblingExplanations = async (questionId, keepId) => {
  if (!questionId || !keepId) return 0

  const snapshot = await db.collection('explanations').where('questionId', '==', questionId).get()
  let deleted = 0

  for (const explanationDoc of snapshot.docs) {
    if (explanationDoc.id === keepId) continue
    await db.collection('explanations').doc(explanationDoc.id).delete()
    deleted += 1
  }

  return deleted
}

/**
 * Upsert an explanation by logical questionId (field), without using questionId as doc id.
 *
 * Client Firestore cannot run collection queries inside transactions, so uniqueness is enforced by:
 * 1) query by questionId
 * 2) update preferred existing doc OR add once
 * 3) re-query and delete siblings if a race created extras
 *
 * Future rewrite should use canonical doc id `explanations/{questionId}` + transaction.get(docRef).
 *
 * @param {object} payload - must include questionId
 * @param {{ preserveVotes?: boolean }} [options]
 * @returns {Promise<{ id: string, created: boolean, cleaned: number }>}
 */
export const upsertExplanationByQuestionId = async (payload, options = {}) => {
  const { preserveVotes = true } = options
  const questionId = payload && payload.questionId

  if (!questionId) {
    throw new Error('upsertExplanationByQuestionId requires payload.questionId')
  }

  const existing = await db.collection('explanations').where('questionId', '==', questionId).get()

  if (!existing.empty) {
    const preferred = pickPreferredExplanationDoc(existing.docs)
    const current = preferred.data() || {}
    const next = {
      ...payload,
      questionId
    }

    if (preserveVotes) {
      next.upvotes = current.upvotes != null ? current.upvotes : (payload.upvotes || 0)
      next.downvotes = current.downvotes != null ? current.downvotes : (payload.downvotes || 0)
    }

    await db.collection('explanations').doc(preferred.id).update(next)
    const cleaned = await deleteSiblingExplanations(questionId, preferred.id)
    return { id: preferred.id, created: false, cleaned }
  }

  const createdRef = await db.collection('explanations').add({
    upvotes: 0,
    downvotes: 0,
    ...payload,
    questionId
  })

  const afterCreate = await db.collection('explanations').where('questionId', '==', questionId).get()
  if (afterCreate.size > 1) {
    const preferred = pickPreferredExplanationDoc(afterCreate.docs)
    const cleaned = await deleteSiblingExplanations(questionId, preferred.id)
    return { id: preferred.id, created: preferred.id === createdRef.id, cleaned }
  }

  return { id: createdRef.id, created: true, cleaned: 0 }
}
